import { Link } from 'react-router';
import { FUTURO_TYPEFACES as F } from '../../foundations/font';
import S from '../../foundations/size';
import { genThemedStyled } from '../../utilities/theme';
import { ColorCodes } from '../../foundations/color';

type themeStyles = {
  textColor: ColorCodes;
  iconColor: ColorCodes;
  iconBorderColor: ColorCodes;
  activeColor: ColorCodes;
  activeIconColor: ColorCodes;
  activeRowColor: ColorCodes;
  optionRowHeaderColor: ColorCodes;
};

const ThemeStyled = genThemedStyled<themeStyles>();

export const ContentWrapper = ThemeStyled.div({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  maxHeight: '20rem',
  overflowY: 'auto',
  padding: S.XXS,
});

export const IconWrapper = ThemeStyled('div', { target: 'icon-wrapper' })<{
  isActive?: boolean;
  theme: themeStyles;
}>(({ isActive, theme }) => ({
  width: S.XXL,
  height: S.XXL,
  minWidth: S.XXL,
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '50%',
  border: isActive ? 'unset' : `1px solid ${theme.iconBorderColor}`,
  backgroundColor: isActive ? theme.activeColor : 'unset',
  color: theme.iconColor,
}));

export const SettingsIconWrapper = ThemeStyled('div', {
  target: 'settings-icon-wrapper',
})(p => ({
  width: S.XXL,
  height: '100%',
  minWidth: S.XXL,
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '50%',
  color: p.theme.iconColor,
  paddingRight: S.XXS,
  paddingLeft: S.XXS,
}));

export const ImageWrapper = ThemeStyled.div<{
  src: string;
  theme: themeStyles;
}>(({ src, theme }) => ({
  width: S.XXL,
  height: S.XXL,
  minWidth: S.XXL,
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '50%',
  border: `1px solid ${theme.iconBorderColor}`,
  background: `url('${src}')`,
  backgroundSize: 'contain',
  backgroundRepeat: 'no-repeat',
}));

export const OptionNameWrapper = ThemeStyled('div', {
  target: 'option-name-wrapper',
})(p => ({
  color: p.theme.textColor,
  display: 'flex',
  alignItems: 'center',
  paddingLeft: S.XS,
  paddingRight: S.XS,
  flexGrow: 1,
}));

export const OptionInnerNameWrapper = ThemeStyled.div(
  {
    display: '-webkit-box',
    textOverflow: 'ellipsis',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  F.H5
);

export const OptionRow = ThemeStyled.div<{
  isActive: boolean;
  theme: themeStyles;
}>(({ isActive, theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  cursor: 'pointer',
  backgroundColor: isActive ? theme.activeRowColor : 'unset',
  borderRadius: isActive ? S.XXXS : 'unset',
  ':hover': {
    backgroundColor: theme.activeRowColor,
    borderRadius: S.XXXS,
  },
  [`:hover > a ${OptionNameWrapper}`]: {
    color: theme.activeIconColor,
  },
  [`:hover > a ${IconWrapper}`]: {
    backgroundColor: theme.activeColor,
    color: theme.activeIconColor,
    border: 'unset',
  },
  [`:hover > a > ${SettingsIconWrapper}`]: {
    color: theme.activeIconColor,
  },
}));

export const OptionRowContentLeft = ThemeStyled.div({
  display: 'flex',
  flexDirection: 'row',
  paddingLeft: S.XXS,
  paddingTop: S.XXS,
  paddingBottom: S.XXS,
  width: '100%',
});

export const OptionContainer = ThemeStyled.div({
  display: 'flex',
  flexDirection: 'column',
});

export const OptionHeader = ThemeStyled.div(
  p => ({
    color: p.theme.optionRowHeaderColor,
    paddingBottom: S.XXXS,
    paddingTop: S.XXS,
    paddingLeft: S.XXS,
  }),
  F.LABEL_SMALL_BOLD
);

export const NavAnchor = ThemeStyled.a({
  width: '100%',
});

export const NavLink = ThemeStyled(Link)({
  width: '100%',
  display: 'flex',
  justifyContent: 'flex-end',
  flexGrow: 1,
});

export const SettingsAnchor = ThemeStyled.a();

export const SettingsLink = ThemeStyled(Link)();
